import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";

const when = (t) => (t ? new Date(t * 1000).toLocaleString() : "-");
const Yes = ({ v }) => <span className={v ? "font-semibold text-green-700" : "font-semibold text-red-600"}>{v ? "Yes" : "No"}</span>;

function AccountCard({ a, mode }) {
  const rows = [["Account ID", a.id], ["Name", a.name || "-"], ["Email", a.email || "-"], ["Country / currency", `${a.country || "-"} / ${(a.currency || "-").toUpperCase()}`], ["Key mode", mode.toUpperCase()]];
  return (
    <div className="grid gap-4 rounded-2xl border border-brand/10 bg-white p-6 md:grid-cols-2" data-testid="stripe-account">
      <dl className="space-y-1.5 text-sm">{rows.map(([k, v]) => <div key={k} className="flex gap-3"><dt className="w-36 text-muted-foreground">{k}</dt><dd className="font-mono" data-testid={`stripe-${k.split(" ")[0].toLowerCase()}`}>{v}</dd></div>)}</dl>
      <dl className="space-y-1.5 text-sm">
        <div className="flex gap-3"><dt className="w-36 text-muted-foreground">Charges enabled</dt><dd data-testid="stripe-charges-enabled"><Yes v={a.charges_enabled} /></dd></div>
        <div className="flex gap-3"><dt className="w-36 text-muted-foreground">Payouts enabled</dt><dd><Yes v={a.payouts_enabled} /></dd></div>
        <div className="flex gap-3"><dt className="w-36 text-muted-foreground">Details submitted</dt><dd><Yes v={a.details_submitted} /></dd></div>
        {a.disabled_reason && <div className="text-red-600" data-testid="stripe-disabled-reason">Disabled: {a.disabled_reason}</div>}
        {a.currently_due?.length > 0 && <div className="text-amber-700">Stripe needs: {a.currently_due.join(", ")}</div>}
      </dl>
    </div>
  );
}

function Table({ title, cols, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-brand/10 bg-white" data-testid={testid}>
      <div className="border-b border-brand/10 px-5 py-3 font-semibold">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-brand/5 text-left"><tr>{cols.map((c) => <th key={c} className="px-4 py-2">{c}</th>)}</tr></thead>
        <tbody>{rows.length ? rows.map((r, i) => <tr key={i} className="border-t border-brand/5">{r.map((c, j) => <td key={j} className="px-4 py-2 align-top">{c}</td>)}</tr>) : <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-muted-foreground">None</td></tr>}</tbody>
      </table>
    </div>
  );
}

export function StripeHealthPanel() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => { setLoading(true); adminApi.get("/admin/stripe-health").then(({ data }) => setD(data)).catch(() => setD({ fatal: "Could not reach the server." })).finally(() => setLoading(false)); };
  useEffect(load, []);
  const errs = d ? ["account_error", "sessions_error", "charges_error", "webhooks_error", "fatal"].filter((k) => d[k]) : [];
  return (
    <div className="space-y-5" data-testid="stripe-health-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Live answers straight from Stripe, using the key this website charges with.</p>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="stripe-health-refresh"><RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
      </div>
      {!d && <p className="text-sm text-muted-foreground">Loading…</p>}
      {errs.map((k) => <p key={k} className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700" data-testid={`stripe-${k}`}>{k.replace("_", " ")}: {d[k]}</p>)}
      {d?.account && <AccountCard a={d.account} mode={d.mode} />}
      {d?.sessions && <Table testid="stripe-sessions" title="Recent checkouts" cols={["Created", "Amount", "Checkout", "Payment", "Card result", "Customer"]}
        rows={d.sessions.map((s) => [when(s.created), `${s.amount} ${(s.currency || "").toUpperCase()}`, s.status, s.payment_status, s.error || s.pi_status || "-", s.email || "-"])} />}
      {d?.charges && <Table testid="stripe-charges" title="Recent card charges" cols={["Created", "Amount", "Status", "Failure", "Bank / Radar outcome"]}
        rows={d.charges.map((c) => [when(c.created), c.amount, c.status, c.failure || "-", c.outcome || "-"])} />}
      {d?.webhooks && <Table testid="stripe-webhooks" title="Webhook endpoints" cols={["URL", "Status", "Events"]} rows={d.webhooks.map((w) => [w.url, w.status, (w.events || []).join(", ")])} />}
    </div>
  );
}
